// =============================================
// Call Center Login Page - מוקד חרום
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, Eye, EyeOff, AlertCircle, Headphones } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { adminApi } from '../../services/api';
import FormLabel from '../../components/common/FormLabel';
import { loginHelpTexts } from '../../constants/helpTexts';
import { callCenterColors } from '../../styles/colors';

const colors = {
  ...callCenterColors,
  primaryDark: '#0f766e',
  error: '#dc2626',
};

export default function CallCenterLogin() {
  const navigate = useNavigate();
  const { setUser, setToken, setRefreshToken, setTokenExpiresAt, setRequiresAgreement } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = 'קיפיקיד — מוקד חירום';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#eab308');
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('יש להזין שם משתמש וסיסמה');
      return;
    }

    setIsLoading(true);

    try {
      const result = await adminApi.adminLogin(username, password);

      if (result.success && result.data) {
        const authData = result.data;
        const serverRole = authData.user?.role || '';

        // רק CallCenter מורשה להיכנס למוקד חרום
        if (serverRole !== 'CallCenter') {
          setError('אין הרשאת מוקד חרום. יש להיכנס דרך ממשק מתאים.');
          setIsLoading(false);
          return;
        }

        setUser({
          id: authData.user?.id?.toString() || '',
          email: username,
          name: `${authData.user?.firstName || ''} ${authData.user?.lastName || ''}`.trim(),
          firstName: authData.user?.firstName,
          lastName: authData.user?.lastName,
          phone: authData.user?.phone,
          role: 'admin',
          publicId: authData.user?.publicId,
          organizationPublicId: authData.user?.organizationPublicId,
          organizationName: authData.user?.organizationName,
          serverRole: 'CallCenter',
        });

        setToken(authData.accessToken);
        if (authData.refreshToken) setRefreshToken(authData.refreshToken);
        if (authData.expiresAt) setTokenExpiresAt(authData.expiresAt);

        // token stored via setToken into sessionStorage-backed Zustand store

        // Check if agreement is required
        if (authData.requiresAgreement) {
          setRequiresAgreement(true, authData.agreementVersion);
          navigate('/agreement-approval', { replace: true });
          return;
        }

        navigate('/callcenter/dashboard');
      } else {
        setError(result.message || 'שם משתמש או סיסמה שגויים');
      }
    } catch {
      setError('שגיאה בהתחברות, נסו שנית');
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 44px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
    transition: 'border-color 0.2s',
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 50%, #99f6e4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(13, 148, 136, 0.15)',
          width: '100%',
          maxWidth: '400px',
          padding: '40px 32px',
        }}
      >
        {/* Logo & Title */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Headphones size={40} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.primary, marginBottom: '4px' }}>
            מוקד חרום
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            התחברות למוקד KipiKid
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              borderRadius: '10px',
              marginBottom: '20px',
            }}
          >
            <AlertCircle size={18} color={colors.error} />
            <span style={{ fontSize: '14px', color: colors.error }}>{error}</span>
          </motion.div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Username */}
          <div>
            <FormLabel text="שם משתמש" helpText={loginHelpTexts.username.tooltip} />
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="שם משתמש"
                style={inputStyle}
                disabled={isLoading}
              />
              <User
                size={18}
                color={colors.textLight}
                style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <FormLabel text="סיסמה" helpText={loginHelpTexts.password.tooltip} />
            <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="סיסמה"
              style={inputStyle}
              disabled={isLoading}
            />
            <Lock
              size={18}
              color={colors.textLight}
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              {showPassword ? (
                <EyeOff size={18} color={colors.textLight} />
              ) : (
                <Eye size={18} color={colors.textLight} />
              )}
            </button>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '16px',
              marginTop: '8px',
              borderRadius: '12px',
              border: 'none',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? 'מתחבר...' : 'התחברות'}
          </motion.button>
        </form>

        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '12px',
            backgroundColor: 'transparent',
            color: colors.textLight,
            border: 'none',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          חזרה לדף הבית
        </button>
      </motion.div>
    </div>
  );
}
